/**
 * Player-facing names and units for item stat modifiers (`statMods`).
 *
 * Shared by the item tooltip and the shop card's one-line summary. Before
 * 2026-10-17 each had its own handling and the tooltip knew 1 of the 7 keys the
 * catalog uses, so players read "lootQualityMult +0.02" (Janeks: "make a proper
 * stat name ... these are seen by the player").
 *
 * Keys are `DerivedStats` fields (backend `character.types.ts`): the server
 * adds `statMods.flat[key]` straight onto that stat (`equipment.formulas`), so
 * the unit here must be the stat's own:
 *   - `pct`:    a multiplier or a 0-based fraction (run speed base 1.0, reload
 *               +0.03 per AGI, loot quality 1 + ...) — 0.05 reads as "+5%";
 *   - `points`: aim precision (PER gives 2 per point; weapons carry 0.45-1.2),
 *               power, max HP;
 *   - `kg`, `perMin`, `sec`: what they say.
 * `lowerIsBetter` flips the colour: −3% crafting time is a perk.
 * `stat-format.test.ts` holds every DerivedStats key to having an entry here.
 */
export type StatUnit = 'pct' | 'points' | 'kg' | 'perMin' | 'sec';

export interface StatFormat {
  label: string;
  unit: StatUnit;
  lowerIsBetter?: boolean;
}

export const STAT_FORMATS: Readonly<Record<string, StatFormat>> = {
  power: { label: 'Power', unit: 'points' },
  carryWeight: { label: 'Carry capacity', unit: 'kg' },
  hpAmount: { label: 'Max health', unit: 'points' },
  hpRegenPerMin: { label: 'Health regen', unit: 'perMin' },
  walkSpeed: { label: 'Walk speed', unit: 'pct' },
  runSpeed: { label: 'Run speed', unit: 'pct' },
  sprintTime: { label: 'Sprint time', unit: 'sec' },
  reloadSpeed: { label: 'Reload speed', unit: 'pct' },
  aimSpeed: { label: 'Aim speed', unit: 'pct' },
  aimPrecision: { label: 'Aim precision', unit: 'points' },
  shootSpeed: { label: 'Fire rate', unit: 'pct' },
  energyRegenPerMin: { label: 'Energy regen', unit: 'perMin' },
  craftTimeMult: { label: 'Crafting time', unit: 'pct', lowerIsBetter: true },
  buildTimeMult: { label: 'Building time', unit: 'pct', lowerIsBetter: true },
  lootQualityMult: { label: 'Loot quality', unit: 'pct' },
  learnRateMult: { label: 'Learning speed', unit: 'pct' },
  // Weapon handling keys (`ItemDef.handling`), should one ever land in statMods.
  reload: { label: 'Reload speed', unit: 'pct' },
  aim: { label: 'Aim speed', unit: 'pct' },
  precision: { label: 'Aim precision', unit: 'points' },
  shoot: { label: 'Fire rate', unit: 'pct' },
  crouchSpeed: { label: 'Crouch speed', unit: 'pct' },
  // Not a DerivedStats key: a worn backpack's capacity, read by the inventory's slot cap.
  carriedSlots: { label: 'Carry slots', unit: 'points' },
};

/** "lootQualityMult" -> "Loot quality": the fallback for a key nobody labelled yet. */
export function humanizeStatKey(key: string): string {
  const words = key.replace(/Mult$|PerMin$/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function trim(n: number): string {
  return String(Math.round(n * 10) / 10);
}

export interface FormattedStat {
  label: string;
  /** Signed, with its unit: "+5%", "-5%", "+1", "+2 kg", "+1.5/min". */
  value: string;
  /** True when the change helps the player (drives the green/red row). */
  good: boolean;
}

/**
 * One modifier, ready to print. `kind` is which bucket of `statMods` it came
 * from: `pct` entries are always fractions of the stat ("+0.1" -> "+10%").
 */
export function formatStatMod(key: string, value: number, kind: 'flat' | 'pct' = 'flat'): FormattedStat {
  const fmt = STAT_FORMATS[key];
  const unit: StatUnit = kind === 'pct' ? 'pct' : fmt?.unit ?? 'points';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const mag = Math.abs(value);
  const body = unit === 'pct' ? `${trim(mag * 100)}%`
    : unit === 'kg' ? `${trim(mag)} kg`
    : unit === 'perMin' ? `${trim(mag)}/min`
    : unit === 'sec' ? `${trim(mag)} s`
    : trim(mag);
  const good = fmt?.lowerIsBetter ? value < 0 : value > 0;
  return { label: fmt?.label ?? humanizeStatKey(key), value: `${sign}${body}`, good };
}

/** Every modifier of an item, flat then pct, in catalog order. */
export function formatStatMods(mods: { flat?: Record<string, number>; pct?: Record<string, number> }): FormattedStat[] {
  return [
    ...Object.entries(mods.flat ?? {}).map(([k, v]) => formatStatMod(k, v, 'flat')),
    ...Object.entries(mods.pct ?? {}).map(([k, v]) => formatStatMod(k, v, 'pct')),
  ];
}

/** "rifle" -> "Rifle rounds", for the Ammo row and the shop line. */
export function ammoLabel(ammoType: string): string {
  return `${ammoType.charAt(0).toUpperCase()}${ammoType.slice(1)} rounds`;
}
